/**
 * Os arquivos soltos no bloco antes de o número da CCB estar gravado (E209.F3.T02, GUS-296 item 12a).
 *
 * Antes, a área de soltar só abria com a CCB gravada, e o número só se gravava ao sair do campo:
 * digitar o número e soltar o ZIP em seguida não punha nada na CCB. Agora a área abre com o número
 * digitado; o que se solta antes da gravação entra nesta espera, a gravação começa, e a espera sai para
 * o envio quando a CCB está gravada. Na declaração o bloco troca de chave (a local vira o id da CCB)
 * e a tela o monta de novo: a espera passa ao bloco novo pela tela (`aEnviar`).
 */
import { useEffect, useRef, useState } from 'react';

export interface EsperaDoBloco {
  /** Os arquivos à espera da gravação do número, os deste bloco e os herdados da declaração. */
  esperando: number;
  /** Guarda o lote até a CCB estar gravada. */
  guardar: (arquivos: readonly File[]) => void;
  /** O que entregar ao bloco da CCB declarada; esvazia a espera local. */
  entregar: () => readonly File[];
}

export function useEsperaDoBloco(opcoes: {
  pronto: boolean;
  herdados: readonly File[] | undefined;
  enviar: (arquivos: readonly File[]) => Promise<void>;
  onEnviados: () => void;
}): EsperaDoBloco {
  const { pronto, herdados, enviar, onEnviados } = opcoes;
  const [espera, setFila] = useState<readonly File[]>([]);
  const esperaAtual = useRef(espera);
  esperaAtual.current = espera;
  // O mesmo arquivo não sai duas vezes (a espera e os herdados se esvaziam numa renderização depois).
  const enviados = useRef(new Set<File>());
  useEffect(() => {
    if (!pronto) return;
    const lote = [...(herdados ?? []), ...espera].filter((f) => !enviados.current.has(f));
    if (herdados !== undefined && herdados.length > 0) onEnviados();
    if (lote.length === 0) return;
    lote.forEach((f) => enviados.current.add(f));
    setFila([]);
    void enviar(lote);
  }, [pronto, herdados, espera, enviar, onEnviados]);
  return {
    esperando: espera.length + (herdados?.length ?? 0),
    guardar: (arquivos) => setFila((atual) => [...atual, ...arquivos]),
    entregar: () => {
      const lote = esperaAtual.current;
      setFila([]);
      return lote;
    },
  };
}
